import React, { useState } from "react";
import { api } from "../utils/api";
import { t } from "../utils/i18n";
import VerifiedBadge from "./VerifiedBadge";
import { HomeIcon } from "./messages/icons";

// Extrait de Create.tsx (composant local à l'origine) pour être réutilisable ailleurs — d'abord
// par FastCreateSheet.tsx, qui a besoin de la même recherche de lieu (OLTODO + adresse libre +
// création inline) que la création d'une publication classique. Signature inchangée.
export type OltodoVenue = { id: string; name: string; type: string; address: string; is_verified: boolean };
// Même liste que components/CreateVenueModal.tsx — champ libre avec suggestions, jamais un choix
// fermé (voir le <input> plus bas) : trop de corps de métier et de formes de société existent
// pour espérer une liste exhaustive.
const VENUE_TYPES = [
  "Bar / Café", "Restaurant", "Boîte de nuit / Discothèque", "Bowling", "Casino",
  "Salle de spectacle", "Théâtre", "Cinéma", "Karting", "Escape game", "Laser game", "Salle de jeux / Arcade",
  "Salle de sport", "Terrain de sport", "Piscine", "Patinoire", "Salle d'escalade", "Golf",
  "Spa / Institut de bien-être", "Salon de coiffure", "Institut de beauté",
  "Musée / Galerie", "Bibliothèque / Médiathèque", "Lieu de culte", "Salle polyvalente", "Studio (danse, musique, yoga...)",
  "Parc / Espace vert", "Ferme / Domaine", "Camping",
  "Hôtel",
  "Commerce / Boutique", "Supermarché / Épicerie", "Centre commercial", "Marché", "Salon professionnel / Foire",
  "Concession automobile", "Garage automobile", "Carrossier", "Pharmacie", "Opticien", "Pressing", "Fleuriste", "Auto-école",
  "Plombier", "Électricien", "Maçon / BTP", "Menuisier", "Peintre en bâtiment", "Chauffagiste", "Serrurier", "Paysagiste", "Couvreur",
  "Cabinet médical", "Cabinet dentaire", "Cabinet de kinésithérapie", "Cabinet d'avocats", "Cabinet comptable",
  "Agence immobilière", "Agence de voyage", "Agence de communication", "Studio photo / vidéo",
  "Notaire", "Architecte", "Consultant / Freelance", "Traiteur", "Banque / Assurance", "Coworking",
  "Société", "Indépendant / Auto-entrepreneur", "Groupe", "Holding", "Franchise", "Startup",
  "Club / Fédération sportive", "ONG / Organisation caritative", "École / Organisme de formation", "Administration / Collectivité",
  "Autre",
];

// showSavedAddresses (opt-in, jamais activé par défaut) : propose le carnet "Mes adresses"
// (pages/MyAddresses.tsx) tant que le champ est vide — voir la même prop côté mobile
// (components/LocationSearch.tsx). Jamais activé sur un point d'usage qui cherche un lieu public.
export default function VenueField({ value, onChange, lang, onNavigate, hideCreate, showSavedAddresses }: { value: string; onChange: (v: string, venueId?: string | null, lat?: number | null, lng?: number | null) => void; lang: string; onNavigate: (path: string) => void; hideCreate?: boolean; showSavedAddresses?: boolean }) {
  const [search, setSearch] = useState(value);
  const [showResults, setShowResults] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState<{ id: string; label: string; address: string; latitude: number | null; longitude: number | null }[]>([]);

  React.useEffect(() => {
    if (!showSavedAddresses) return;
    api.get("/addresses/mine").then(d => setSavedAddresses(d?.addresses || [])).catch(() => {});
  }, [showSavedAddresses]);
  const [showAddVenue, setShowAddVenue] = useState(false);
  const [addVenueSuccess, setAddVenueSuccess] = useState(false);
  const [linkedVenue, setLinkedVenue] = useState<OltodoVenue | null>(null);
  const [oltodoVenues, setOltodoVenues] = useState<OltodoVenue[]>([]);
  // Form nouveau lieu
  const [newVenueName, setNewVenueName] = useState("");
  const [newVenueType, setNewVenueType] = useState("");
  const [newVenueAddress, setNewVenueAddress] = useState("");
  const [newVenuePhone, setNewVenuePhone] = useState("");
  const [newVenueWebsite, setNewVenueWebsite] = useState("");
  const [isOwner, setIsOwner] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [submittingVenue, setSubmittingVenue] = useState(false);

  const [nominatimResults, setNominatimResults] = React.useState<any[]>([]);
  const [nominatimLoading, setNominatimLoading] = React.useState(false);
  const searchSeq = React.useRef(0);

  // Recherche temporisée (debounce) : sans ce délai, chaque frappe déclenche un appel
  // à l'API OpenStreetMap, qui la limite/bloque très vite (politique 1 req/s) — c'était
  // la cause du "impossible de trouver Le Puy-en-Velay" en tapant normalement.
  React.useEffect(() => {
    const mySeq = ++searchSeq.current;
    if (search.length < 2) { setOltodoVenues([]); setNominatimResults([]); return; }
    setNominatimLoading(true);
    const timer = setTimeout(async () => {
      try {
        const data = await api.get(`/venues/search?q=${encodeURIComponent(search)}`);
        if (mySeq === searchSeq.current) setOltodoVenues(data?.venues || []);
      } catch { if (mySeq === searchSeq.current) setOltodoVenues([]); }

      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(search)}&format=json&addressdetails=1&limit=5&accept-language=fr`,
          { headers: { "Accept-Language": "fr" } }
        );
        const data = await res.json();
        if (mySeq === searchSeq.current) setNominatimResults(data);
      } catch { if (mySeq === searchSeq.current) setNominatimResults([]); }
      finally { if (mySeq === searchSeq.current) setNominatimLoading(false); }
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const handleSelect = (venue: OltodoVenue) => {
    setLinkedVenue(venue);
    setSearch(venue.name);
    onChange(venue.name, venue.id, (venue as any).latitude ?? null, (venue as any).longitude ?? null);
    setShowResults(false);
  };

  const handleFreeText = (v: string) => {
    setSearch(v);
    onChange(v, null);
    setLinkedVenue(null);
    setShowResults(v.length > 1);
  };

  const handleSubmitNewVenue = async () => {
    setSubmittingVenue(true);
    try {
      const geo = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(newVenueAddress)}&format=json&limit=1`).then(r => r.json()).catch(() => []);
      const latitude = geo?.[0]?.lat ? Number(geo[0].lat) : null;
      const longitude = geo?.[0]?.lon ? Number(geo[0].lon) : null;
      const data = await api.post("/venues", {
        name: newVenueName, type: newVenueType, address: newVenueAddress,
        phone: newVenuePhone || null, website: newVenueWebsite || null, is_owner: isOwner,
        latitude, longitude,
      });
      if (data?.venue) {
        setLinkedVenue({ id: data.venue.id, name: data.venue.name, type: data.venue.type, address: data.venue.address, is_verified: false });
        onChange(data.venue.name, data.venue.id, latitude, longitude);
      }
    } catch {
      // ignore, on affiche quand même la confirmation ci-dessous
    } finally {
      setSubmittingVenue(false);
    }
    setAddVenueSuccess(true);
    setSearch(newVenueName);
    setTimeout(() => { setShowAddVenue(false); setAddVenueSuccess(false); }, 2000);
  };

  const canSubmitVenue = newVenueName && newVenueType && newVenueAddress && !submittingVenue;

  if (showAddVenue) {
    return (
      <div style={{ backgroundColor: "#F8F8F8", borderRadius: 14, padding: 20, border: "1px solid #F0F0F0" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>{t(lang, "add_venue") || "Ajouter un lieu"}</div>
          <button onClick={() => setShowAddVenue(false)} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 18, color: "#999" }}>✕</button>
        </div>

        {addVenueSuccess ? (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#0a0a0a" }}>{t(lang, "venue_success") || "Lieu proposé ! Il sera vérifié par OLTODO."}</div>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#666", display: "block", marginBottom: 6 }}>{t(lang, "venue_name") || "Nom du lieu"} *</label>
              <input value={newVenueName} onChange={e => setNewVenueName(e.target.value)} placeholder="Ex: Salle Pleyel, Le Five FC..." style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #E0E0E0", fontSize: 14, outline: "none" }} />
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#666", display: "block", marginBottom: 6 }}>{t(lang, "venue_type") || "Type de lieu"} *</label>
              <div style={{ position: "relative" }}>
                <input
                  value={newVenueType}
                  onChange={e => { setNewVenueType(e.target.value); setShowTypeDropdown(true); }}
                  onFocus={() => setShowTypeDropdown(true)}
                  placeholder="Ex: Bowling, Boîte de nuit, Plombier, Holding..."
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #E0E0E0", fontSize: 14, outline: "none", boxSizing: "border-box" }}
                />
                {showTypeDropdown && (
                  <>
                    <div onClick={() => setShowTypeDropdown(false)} style={{ position: "fixed", inset: 0, zIndex: 199 }} />
                    <div style={{ position: "absolute", top: "100%", left: 0, right: 0, backgroundColor: "white", border: "1px solid #E0E0E0", borderRadius: 8, zIndex: 200, boxShadow: "0 4px 20px rgba(0,0,0,0.1)", maxHeight: 260, overflowY: "auto" }}>
                      {(newVenueType.trim() ? VENUE_TYPES.filter(v => v.toLowerCase().includes(newVenueType.trim().toLowerCase())) : VENUE_TYPES).slice(0, 6).map(type => (
                        <div key={type} onClick={() => { setNewVenueType(type); setShowTypeDropdown(false); }} style={{ padding: "10px 14px", cursor: "pointer", fontSize: 14, backgroundColor: newVenueType === type ? "#F0F0F0" : "transparent" }}>{type}</div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#666", display: "block", marginBottom: 6 }}>{t(lang, "venue_address") || "Adresse"} *</label>
              <input value={newVenueAddress} onChange={e => setNewVenueAddress(e.target.value)} placeholder="Ex: 12 rue des Sports, Paris 15" style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #E0E0E0", fontSize: 14, outline: "none" }} />
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#666", display: "block", marginBottom: 6 }}>{t(lang, "venue_phone") || "Téléphone (optionnel)"}</label>
              <input value={newVenuePhone} onChange={e => setNewVenuePhone(e.target.value)} placeholder="+33 1 23 45 67 89" style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #E0E0E0", fontSize: 14, outline: "none" }} />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#666", display: "block", marginBottom: 6 }}>{t(lang, "venue_website") || "Site web (optionnel)"}</label>
              <input value={newVenueWebsite} onChange={e => setNewVenueWebsite(e.target.value)} placeholder="www.monlieu.fr" style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #E0E0E0", fontSize: 14, outline: "none" }} />
            </div>

            {/* Toggle propriétaire */}
            <div onClick={() => setIsOwner(!isOwner)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", backgroundColor: isOwner ? "#F0F0F0" : "#F8F8F8", borderRadius: 12, border: `1px solid ${isOwner ? "#0a0a0a" : "#E0E0E0"}`, cursor: "pointer", marginBottom: 16 }}>
              <div style={{ width: 44, height: 24, borderRadius: 12, backgroundColor: isOwner ? "#0a0a0a" : "#E0E0E0", position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
                <div style={{ position: "absolute", top: 2, left: isOwner ? 22 : 2, width: 20, height: 20, borderRadius: "50%", backgroundColor: "white", transition: "left 0.2s" }} />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: isOwner ? "#0a0a0a" : "#0a0a0a" }}>{t(lang, "venue_owner") || "C'est mon établissement"}</div>
                <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>{t(lang, "venue_owner_sub") || "Vous pourrez le gérer depuis votre profil"}</div>
              </div>
            </div>

            <button onClick={handleSubmitNewVenue} disabled={!canSubmitVenue} style={{ width: "100%", padding: "12px", borderRadius: 12, border: "none", backgroundColor: canSubmitVenue ? "#0a0a0a" : "#E0E0E0", color: "white", fontSize: 14, fontWeight: 600, cursor: canSubmitVenue ? "pointer" : "not-allowed" }}>
              {submittingVenue ? "Envoi..." : (t(lang, "venue_submit") || "Proposer ce lieu")}
            </button>
          </>
        )}
      </div>
    );
  }

  return (
    <div style={{ position: "relative" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "11px 12px", borderRadius: 10, border: `1px solid ${linkedVenue ? "#0a0a0a" : "#E0E0E0"}`, backgroundColor: linkedVenue ? "#F0F0F0" : "#F8F8F8", marginBottom: 6 }}>
        <input value={search} onChange={e => handleFreeText(e.target.value)} onFocus={() => { if (search.length > 1 || (showSavedAddresses && savedAddresses.length > 0)) setShowResults(true); }} placeholder="Tapez une adresse ou un lieu..." style={{ flex: 1, border: "none", outline: "none", fontSize: 14, backgroundColor: "transparent", color: "#0a0a0a" }} />
        {linkedVenue && <span style={{ color: "#0a0a0a", fontWeight: 700 }}>✓</span>}
        {search && <button onClick={() => { setSearch(""); onChange("", null); setLinkedVenue(null); setShowResults(false); }} style={{ border: "none", background: "none", cursor: "pointer", color: "#999", fontSize: 16, lineHeight: 1 }}>✕</button>}
      </div>

      {/* Lieux certifiés OLTODO — recouvrement (position:absolute) plutôt qu'un bloc en flux
          normal : sinon chaque frappe pousse tout le contenu suivant vers le bas puis le fait
          remonter à la fermeture, un "ça se balade" particulièrement visible dès que plusieurs
          VenueField s'enchaînent dans un même formulaire (ex: départ + destination d'un Move). */}
      {showResults && (
        <>
          <div onClick={() => setShowResults(false)} style={{ position: "fixed", inset: 0, zIndex: 199 }} />
          <div style={{ position: "absolute", top: "100%", left: 0, right: 0, backgroundColor: "white", border: "1px solid #E0E0E0", borderRadius: 12, marginBottom: 8, overflow: "hidden", overflowY: "auto", maxHeight: 340, boxShadow: "0 4px 20px rgba(0,0,0,0.1)", zIndex: 200 }}>
          {showSavedAddresses && search.trim().length === 0 && savedAddresses.length > 0 && (
            <>
              <div style={{ padding: "8px 14px", fontSize: 11, fontWeight: 600, color: "#999", backgroundColor: "#F8F8F8", borderBottom: "1px solid #F0F0F0" }}>
                {t(lang, "side_menu_my_addresses")}
              </div>
              {savedAddresses.map(a => (
                <div key={a.id} onClick={() => { setSearch(a.address); onChange(a.address, null, a.latitude, a.longitude); setShowResults(false); }} style={{ padding: "12px 14px", cursor: "pointer", borderBottom: "1px solid #F5F5F5" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14, fontWeight: 700 }}><HomeIcon size={14} color="#0a0a0a" /> {a.label}</div>
                  <div style={{ fontSize: 11, color: "#999", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.address}</div>
                </div>
              ))}
            </>
          )}
          {oltodoVenues.length > 0 && (
            <>
              <div style={{ padding: "8px 14px", fontSize: 11, fontWeight: 600, color: "#999", backgroundColor: "#F8F8F8", borderBottom: "1px solid #F0F0F0" }}>
                Lieux OLTODO
              </div>
              {oltodoVenues.map(venue => (
                <div key={venue.id} onClick={() => handleSelect(venue)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", cursor: "pointer", borderBottom: "1px solid #F5F5F5" }}>
                  <div style={{ width: 36, height: 36, borderRadius: 8, backgroundColor: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{venue.name.slice(0,2).toUpperCase()}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 14, fontWeight: 600 }}>{venue.name}</span>
                      {venue.is_verified && <VerifiedBadge size={13} />}
                    </div>
                    <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>{venue.type} · {venue.address}</div>
                  </div>
                </div>
              ))}
            </>
          )}
          {/* Résultats OpenStreetMap */}
          {nominatimLoading && (
            <div style={{ padding: "12px 14px", fontSize: 13, color: "#999", textAlign: "center" }}>
              Recherche en cours...
            </div>
          )}
          {!nominatimLoading && nominatimResults.length > 0 && (
            <>
              <div style={{ padding: "8px 14px", fontSize: 11, fontWeight: 600, color: "#999", backgroundColor: "#F8F8F8", borderBottom: "1px solid #F0F0F0", display: "flex", alignItems: "center", gap: 6 }}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="#0a0a0a"><circle cx="12" cy="12" r="10"/></svg>
                Lieux trouvés
              </div>
              {nominatimResults.map((result, i) => {
                const name = result.namedetails?.name || result.display_name.split(",")[0];
                const address = result.display_name.split(",").slice(1, 3).join(",").trim();
                return (
                  <div key={i} onClick={() => { setSearch(name + ", " + address); onChange(name + ", " + address, null, Number(result.lat), Number(result.lon)); setLinkedVenue(null); setShowResults(false); setNominatimResults([]); }} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", cursor: "pointer", borderBottom: "1px solid #F5F5F5" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</div>
                      <div style={{ fontSize: 11, color: "#999", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{address}</div>
                    </div>
                  </div>
                );
              })}
              <div style={{ padding: "6px 14px", display: "flex", alignItems: "center", gap: 4, backgroundColor: "#F8F8F8" }}>
                <svg width="10" height="10" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z" fill="#999"/></svg>
                <span style={{ fontSize: 10, color: "#999" }}>© OpenStreetMap contributors</span>
              </div>
            </>
          )}
          {/* Utiliser texte libre */}
          {search.trim().length > 0 && (
            <div onClick={() => { setShowResults(false); }} style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", cursor: "pointer", borderBottom: "1px solid #F5F5F5" }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>Utiliser "{search}"</div>
                <div style={{ fontSize: 11, color: "#999" }}>Adresse libre</div>
              </div>
            </div>
          )}
          {/* Ajouter un nouveau lieu */}
          {!hideCreate && search.trim().length > 0 && (
            <div onClick={() => { setShowResults(false); setShowAddVenue(true); setNewVenueName(search); }} style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", cursor: "pointer", backgroundColor: "#F8F8F8" }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#0a0a0a" }}>{t(lang, "create_place") || "Créer un nouveau lieu"}</div>
                <div style={{ fontSize: 11, color: "#999" }}>Il sera ajouté à OLTODO après vérification</div>
              </div>
            </div>
          )}
          </div>
        </>
      )}

      {/* Lieu lié */}
      {linkedVenue && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, backgroundColor: "#F0F0F0", borderRadius: 10, padding: "8px 12px", marginBottom: 8 }}>
          <span style={{ fontSize: 12, color: "#0a0a0a", fontWeight: 600 }}>✓ Lieu certifié OLTODO lié</span>
          <button onClick={() => onNavigate(`/venue/${linkedVenue.id}`)} style={{ marginLeft: "auto", fontSize: 11, color: "#0a0a0a", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}>Voir la page</button>
        </div>
      )}

      {/* Bouton ajouter lieu (si pas encore de résultats) */}
      {!hideCreate && !showResults && !linkedVenue && (
        <button onClick={() => setShowAddVenue(true)} style={{ color: "#0a0a0a", background: "none", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, padding: "4px 0" }}>
          {t(lang, "add_venue") || "Ajouter un lieu OLTODO"}
        </button>
      )}
    </div>
  );
}
